<script setup lang="ts">
/* =============================================================
   BERTH PROFILE (ship-operator side)
   =============================================================
   One berth, as a ship operator planning a call sees it: position,
   limits, amenities, prices, when it is shut, and -- for the ship
   and stay in the search -- whether it can be booked and what it
   costs. Then nearby alternatives, because the brief's point is
   that the next berth along might be cheaper, free, or better
   equipped.

   The search context rides along in the URL, so changing the ship
   or the dates here re-prices the page, and "Back to search"
   returns to the same search.
   ============================================================= */

import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import Badge from '@/components/ui/Badge.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import DetailList from '@/components/ui/DetailList.vue'
import type { DetailRow } from '@/components/ui/DetailList.vue'
import MapView from '@/components/MapView.vue'
import SearchControls from '@/components/search/SearchControls.vue'
import StayQuote from '@/components/search/StayQuote.vue'
import BerthResultCard from '@/components/search/BerthResultCard.vue'
import { routes, to } from '@/config/routes'
import { getBerth } from '@/lib/berths'
import { listRates } from '@/lib/rates'
import { listBlocks } from '@/lib/blocks'
import { listVessels } from '@/lib/vessels'
import { searchAroundBerth } from '@/lib/search'
import { useAmenities } from '@/lib/amenities'
import { useCountries } from '@/lib/countries'
import {
  blockReasonLabel,
  formatDateTime,
  rateConditionText,
  ratePriceText,
} from '@/lib/bookingLabels'
import { bookingModeText, rankResults, resultStatus, shortPrice } from '@/lib/searchLabels'
import { errorMessage } from '@/lib/errors'
import { useSearchContext } from '@/lib/useSearchContext'
import { useAuth } from '@/lib/auth/useAuth'
import { useDocumentTitle } from '@/lib/composables/useDocumentTitle'
import type { MapPoint } from '@/lib/map'
import type { Berth, BerthBlock, BerthRate, BerthResult, Vessel } from '@/lib/types'

const route = useRoute()
const { context, amenities, hasStay, update } = useSearchContext()
const { user } = useAuth()
const companyId = () => user.value?.companyId ?? 0
const { amenityLabel } = useAmenities()
const { countryName } = useCountries()

const id = computed(() => Number(route.params.id))

const berth = ref<Berth | null>(null)
const rates = ref<BerthRate[]>([])
const blocks = ref<BerthBlock[]>([])
const vessels = ref<Vessel[]>([])
const result = ref<BerthResult | null>(null)
const nearby = ref<BerthResult[]>([])
const loading = ref(true)
const error = ref('')

useDocumentTitle(() => berth.value?.name ?? 'Berth')

const vessel = computed(() => vessels.value.find((v) => v.id === context.value.vessel) ?? null)

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    const [b, r, bl, v] = await Promise.all([getBerth(id.value), listRates(id.value), listBlocks(id.value), listVessels(companyId())])
    berth.value = b
    rates.value = r
    // Only what is still ahead matters to someone planning a call.
    blocks.value = bl.filter((x) => new Date(x.ends_at).getTime() > Date.now())
    vessels.value = v
    await quote()
  } catch (e) {
    error.value = errorMessage(e, 'Could not load the berth.')
  } finally {
    loading.value = false
  }
}

async function quote(): Promise<void> {
  const b = berth.value
  if (!b) return
  try {
    const found = await searchAroundBerth(b, {
      radiusKm: 3,
      vessel: vessel.value,
      amenities: amenities.value,
      arrival: hasStay.value ? context.value.from : null,
      departure: hasStay.value ? context.value.to : null,
    })
    result.value = found.result
    nearby.value = rankResults(found.nearby, hasStay.value).slice(0, 4)
  } catch (e) {
    error.value = errorMessage(e, 'Could not price the stay.')
  }
}

onMounted(load)
watch(id, load)
watch([vessel, () => context.value.from, () => context.value.to, amenities], quote, { deep: true })

const status = computed(() => (result.value ? resultStatus(result.value, hasStay.value) : null))

const rows = computed<DetailRow[]>(() => {
  const b = berth.value
  if (!b) return []
  const metres = (n: number | null) => (n === null ? null : `${n} m`)
  return [
    b.company ? { label: 'Owner', value: b.company.name } : { label: 'Owner', empty: true },
    { label: 'Location', value: `${b.city}, ${countryName(b.country_code)}` },
    metres(b.max_length_m) ? { label: 'Max length', value: metres(b.max_length_m) as string, mono: true } : { label: 'Max length', empty: true },
    metres(b.max_width_m) ? { label: 'Max width', value: metres(b.max_width_m) as string, mono: true } : { label: 'Max width', empty: true },
    metres(b.max_draft_m) ? { label: 'Max draft', value: metres(b.max_draft_m) as string, mono: true } : { label: 'Max draft', empty: true },
    b.max_vessels ? { label: 'Vessels at once', value: String(b.max_vessels), mono: true } : { label: 'Vessels at once', empty: true },
    b.amenities.length ? { label: 'Amenities', tags: b.amenities.map(amenityLabel) } : { label: 'Amenities', empty: true },
    { label: 'Booking', value: bookingModeText(b.booking_mode, b.response_hours) },
  ]
})

const points = computed<MapPoint[]>(() => {
  const b = berth.value
  if (!b) return []
  const here: MapPoint = {
    id: b.id,
    lat: b.latitude,
    lng: b.longitude,
    title: b.name,
    label: hasStay.value ? shortPrice(result.value?.price_eur ?? null) : undefined,
    tone: 'selected',
  }
  const others: MapPoint[] = nearby.value.map((r) => ({
    id: r.id,
    lat: r.latitude,
    lng: r.longitude,
    title: r.name,
    subtitle: resultStatus(r, hasStay.value).label,
    label: hasStay.value ? shortPrice(r.price_eur) : undefined,
    tone: resultStatus(r, hasStay.value).bookable ? 'default' : 'muted',
  }))
  return [here, ...others]
})

const backToSearch = computed(() => to.search({ ...context.value, city: context.value.city ?? berth.value?.city }))
</script>

<template>
  <p v-if="loading && !berth" class="page-status">Loading…</p>

  <template v-else-if="berth">
    <PageHeader :title="berth.name" :description="`${berth.city} · ${berth.company?.name ?? ''}`">
      <template #actions>
        <Button variant="ghost" icon="chevronRight" :to="backToSearch" class="back-link">Search</Button>
        <Button
          icon="bookings"
          :to="status?.bookable ? to.book(berth.id, context) : undefined"
          :disabled="!status?.bookable"
        >
          Book this berth
        </Button>
      </template>
    </PageHeader>

    <Alert v-if="error" tone="danger" class="page-alert">{{ error }}</Alert>

    <div class="layout">
      <div class="main">
        <Panel title="Your stay" description="Change the ship or the dates and the price updates.">
          <SearchControls :context="context" :vessels="vessels" @update="update" />
          <div v-if="result" class="quoteBox">
            <StayQuote :result="result" :has-stay="hasStay" :rates="rates" :from="context.from" :to="context.to" />
            <Button
              v-if="status?.bookable"
              icon="bookings"
              :to="to.book(berth.id, context)"
              class="quoteBook"
            >
              Book this berth
            </Button>
          </div>
        </Panel>

        <Panel title="About this berth">
          <DetailList :rows="rows" />
        </Panel>

        <Panel title="Prices" description="The first rule that fits your ship and stay sets the price. Every period started is charged in full. Net of VAT.">
          <p v-if="rates.length === 0" class="empty">No prices published.</p>
          <ol v-else class="rules">
            <li v-for="(r, i) in rates" :key="r.id" :class="{ active: r.id === result?.rate_id && hasStay }">
              <span class="ruleNo">{{ i + 1 }}</span>
              <span class="ruleWhen">{{ rateConditionText(r) }}</span>
              <span class="rulePrice">{{ ratePriceText(r) }}</span>
            </li>
          </ol>
        </Panel>

        <Panel title="Closed" description="Times this berth cannot be booked.">
          <p v-if="blocks.length === 0" class="empty">Nothing announced.</p>
          <ul v-else class="blocks">
            <li v-for="b in blocks" :key="b.id">
              <Badge tone="warning">{{ blockReasonLabel(b.reason) }}</Badge>
              <span>{{ formatDateTime(b.starts_at) }} → {{ formatDateTime(b.ends_at) }}</span>
            </li>
          </ul>
        </Panel>
      </div>

      <aside class="side">
        <div class="mapWrap">
          <MapView :points="points" height="18rem" />
        </div>

        <Panel title="Nearby alternatives" description="Within 3 km, for the same ship and stay.">
          <p v-if="nearby.length === 0" class="empty">No other berths nearby.</p>
          <div v-else class="alts">
            <BerthResultCard
              v-for="r in nearby"
              :key="r.id"
              :result="r"
              :has-stay="hasStay"
              :to="to.berthProfile(r.id, context)"
              :compared="false"
              @toggle-compare="$router.push(to.compare([berth.id, r.id], context))"
            />
          </div>
        </Panel>
      </aside>
    </div>
  </template>

  <template v-else>
    <Alert v-if="error" tone="danger" class="page-alert">{{ error }}</Alert>
    <EmptyState v-else icon="anchor" title="Berth not found" description="The link may be wrong.">
      <template #action>
        <Button :to="routes.search">Find a berth</Button>
      </template>
    </EmptyState>
  </template>
</template>

<style scoped>
.layout {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(18rem, 1fr);
  gap: var(--space-md);
  align-items: start;
}

.main,
.side {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  min-width: 0;
}

.mapWrap {
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.quoteBox {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: var(--space-sm);
  margin-top: var(--space-md);
  padding-top: var(--space-md);
  border-top: var(--border-width) solid var(--color-border);
}

.empty {
  font-size: var(--text-sm);
  color: var(--color-text-muted);
}

.rules {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
  margin: 0;
  padding: 0;
  list-style: none;
}

.rules li {
  display: grid;
  grid-template-columns: 1.5rem minmax(0, 1fr) auto;
  gap: var(--space-xs);
  align-items: baseline;
  padding: var(--space-2xs) var(--space-xs);
  font-size: var(--text-sm);
  border-radius: var(--radius-sm);
}

.rules li.active {
  background-color: var(--color-accent-soft);
}

.ruleNo {
  font-size: var(--text-xs);
  color: var(--color-text-faint);
}

.ruleWhen {
  color: var(--color-text-muted);
}

.rulePrice {
  font-weight: var(--weight-semibold);
  color: var(--color-heading);
  white-space: nowrap;
}

.blocks {
  display: flex;
  flex-direction: column;
  gap: var(--space-2xs);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--text-sm);
}

.blocks li {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-xs);
}

.alts {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
}

@media (max-width: 62rem) {
  .layout {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
